import { useState, useMemo, useEffect, useRef } from "react";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { EmptyState } from "@/components/ui/EmptyState";
import { LinkPreviewCard } from "@/components/ui/LinkPreviewCard";
import { DownloadableImage } from "@/components/ui/DownloadableImage";
import { relativeDate } from "@/lib/humanDate";
import { fromTimestamp } from "@/lib/firestore";
import { isEdited } from "@/lib/postEdit";
import { togglePin, deletePost } from "@/lib/postActions";
import { CreatePostModal } from "@/components/features/CreatePostModal";
import type { HouseholdPost } from "@/lib/models";
import { Link, useNavigate } from "react-router-dom";

const POSTS_PAGE_SIZE = 25;
const LOAD_MORE_THRESHOLD = 5;

export function PostsPage() {
  const { householdId, members, profile, loading } = useHouseholdContext();
  const t = useTranslations();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [visibleCount, setVisibleCount] = useState(POSTS_PAGE_SIZE);
  const [searchText, setSearchText] = useState("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data: posts, loading: postsLoading } =
    useFirestoreCollection<HouseholdPost>(
      householdId ? `households/${householdId}/posts` : null
    );

  // Mirrors iOS `Views/Posts/PostView.swift:41-44`: pinned posts first,
  // then unpinned by created_at desc.
  const sortedPosts = useMemo(() => {
    const byTimeDesc = (a: HouseholdPost, b: HouseholdPost) => {
      const aTime = fromTimestamp(a.created_at)?.getTime() ?? 0;
      const bTime = fromTimestamp(b.created_at)?.getTime() ?? 0;
      return bTime - aTime;
    };
    const pinned = posts.filter((p) => p.is_pinned).sort(byTimeDesc);
    const unpinned = posts.filter((p) => !p.is_pinned).sort(byTimeDesc);
    return [...pinned, ...unpinned];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return sortedPosts;
    return sortedPosts.filter((p) => {
      const title = (p.post_title ?? "").toLowerCase();
      const message = (p.post_message ?? "").toLowerCase();
      return title.includes(q) || message.includes(q);
    });
  }, [sortedPosts, searchText]);

  const visiblePosts = useMemo(
    () => filteredPosts.slice(0, visibleCount),
    [filteredPosts, visibleCount]
  );

  // Auto-expand the visible window when the user scrolls near the end
  // (mirrors iOS PostView visible-posts limit and Android POSTS_PAGE_SIZE
  // / LOAD_MORE_THRESHOLD pattern).
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) =>
            Math.min(c + POSTS_PAGE_SIZE, filteredPosts.length)
          );
        }
      },
      { rootMargin: `${LOAD_MORE_THRESHOLD * 80}px` }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [filteredPosts.length]);

  // Close any open row-action menu when clicking outside.
  useEffect(() => {
    if (!openMenuId) return;
    const onClick = () => setOpenMenuId(null);
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [openMenuId]);

  const findMember = (uid: string) => members.find((m) => m.uid === uid);

  const handleTogglePin = async (post: HouseholdPost) => {
    if (!householdId || !post.uid || !profile?.uid) return;
    setOpenMenuId(null);
    await togglePin({
      householdId,
      postId: post.uid,
      pinnedBy: profile.uid,
      isPinned: post.is_pinned ?? false,
    });
  };

  const handleConfirmDelete = async () => {
    if (!householdId || !confirmDeleteId) return;
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    await deletePost(householdId, id);
  };

  if (loading || postsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-2xl font-bold">{t("posts.title")}</h1>

      {/* Search bar — mirrors iOS `.searchable` modifier on PostView */}
      {sortedPosts.length > 0 && (
        <div className="relative">
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t("posts.search.placeholder")}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
          />
          <svg
            className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>
      )}

      {sortedPosts.length === 0 ? (
        <Card>
          <EmptyState
            icon="📝"
            title={t("posts.empty.title")}
            buttonLabel={t("posts.empty.button")}
            onButtonClick={() => setShowCreate(true)}
          />
        </Card>
      ) : filteredPosts.length === 0 ? (
        <Card>
          <EmptyState
            icon="🔍"
            title={t("posts.search.empty.title")}
            subtitle={t("posts.search.empty.subtitle")}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {visiblePosts.map((post) => {
            const author = findMember(post.created_by);
            const date = fromTimestamp(post.created_at);
            const isCreator = post.created_by === profile?.uid;

            return (
              <div key={post.uid} className="relative">
                <Link to={`/posts/${post.uid}`} className="block">
                  <Card>
                    <div className="flex items-start gap-3">
                      <Avatar
                        src={author?.profile_url}
                        alt={author?.nickname ?? author?.name ?? ""}
                        size={40}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold text-sm truncate">
                            {author?.nickname ?? author?.name ?? t("posts.author.unknown")}
                          </p>
                          <div className="flex items-center gap-2 shrink-0">
                            {post.is_pinned && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                title={t("posts.pinnedBadge")}
                              >
                                📌 {t("posts.pinnedBadge")}
                              </span>
                            )}
                            {date && (
                              <span className="text-xs text-gray-400">
                                {relativeDate(date)}
                                {isEdited(post) && (
                                  <>
                                    {" "}
                                    <span className="italic">
                                      {t("posts.edited")}
                                    </span>
                                  </>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="font-bold text-base mt-1">
                          {post.post_title}
                        </p>
                        {post.post_message && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {post.post_message}
                          </p>
                        )}
                        {/* Image thumbnails — mirrors iOS PostView feed
                            cards which render `PostImageGallery(imageUrls:)`
                            inline. */}
                        {post.images && post.images.length > 0 && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            {post.images.slice(0, 4).map((url, i) => (
                              <DownloadableImage
                                key={i}
                                src={url}
                                alt={t("posts.detail.imageAlt", { n: i + 1 })}
                                className="rounded-lg overflow-hidden max-h-40"
                              />
                            ))}
                          </div>
                        )}
                        {/* Link preview on post card */}
                        <LinkPreviewCard text={post.post_message} compact />
                        {/* Reaction pills + reply count */}
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          {post.reactions && Object.entries(post.reactions)
                            .filter(([, users]) => users.length > 0)
                            .map(([emoji, users]) => (
                              <span
                                key={emoji}
                                className="inline-flex items-center gap-0.5 text-xs text-gray-500"
                              >
                                {emoji === "love" ? "❤️" : emoji === "haha" ? "😂" : emoji === "celebrate" ? "🎉" : emoji === "wow" ? "😮" : emoji === "sad" ? "😢" : emoji === "fire" ? "🔥" : emoji}
                                {" "}{users.length}
                              </span>
                            ))
                          }
                          {post.num_replies > 0 && (
                            <span className="text-xs text-gray-400">
                              {t(
                                post.num_replies === 1
                                  ? "posts.replies.one"
                                  : "posts.replies.other",
                                { count: post.num_replies },
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>

                {/* Row actions — pin/unpin for everyone, edit/delete for
                    the creator. Mirrors iOS `PostView.swift:79-110`
                    swipe + context menu. */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpenMenuId(openMenuId === post.uid ? null : post.uid ?? null);
                  }}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-center"
                  aria-label={t("posts.actions.menu")}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="5" cy="12" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="19" cy="12" r="2" />
                  </svg>
                </button>
                {openMenuId === post.uid && (
                  <div
                    className="absolute top-10 right-2 z-10 min-w-[160px] rounded-lg shadow-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-white/10 py-1 text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => handleTogglePin(post)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5"
                    >
                      {post.is_pinned ? t("posts.actions.unpin") : t("posts.actions.pin")}
                    </button>
                    {isCreator && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setOpenMenuId(null);
                            navigate(`/posts/${post.uid}`);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5"
                        >
                          {t("posts.actions.edit")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setOpenMenuId(null);
                            setConfirmDeleteId(post.uid ?? null);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5 text-red-600 dark:text-red-400"
                        >
                          {t("posts.actions.delete")}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {visibleCount < filteredPosts.length && (
        <div ref={sentinelRef} className="h-8" aria-hidden="true" />
      )}

      {/* Floating Action Button */}
      <button
        onClick={() => setShowCreate(true)}
        className="fixed bottom-24 right-6 sm:bottom-8 w-14 h-14 rounded-full bg-[color:var(--accent-color)] text-white shadow-lg flex items-center justify-center text-2xl hover:opacity-90 transition-opacity"
        aria-label={t("posts.fab.aria")}
      >
        +
      </button>

      {showCreate && (
        <CreatePostModal onClose={() => setShowCreate(false)} />
      )}

      {/* Delete confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setConfirmDeleteId(null)}
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 p-5 shadow-xl text-gray-900 dark:text-gray-100">
            <h3 className="font-bold text-base">
              {t("posts.deleteConfirm.title")}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t("posts.deleteConfirm.message")}
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 dark:hover:bg-white/5"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700"
              >
                {t("posts.actions.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
